import $ from 'jquery';
import Swal from 'sweetalert2';
import { pricingEntryOf, suggestedNet } from './predictionPricing';

/**
 * La politique tarifaire à l'échelle du tableau de prévisions : une étude rouverte apprend
 * ce que ses articles valent chez son client, et un changement de société propose de
 * reprendre les prix. Transposé du formulaire de lignes de devis, en prix net et en marge.
 * Voir docs/DRI/Phase1/politique-tarifaire-etudes.md, § 8.
 */
export default {
    props: ['pricingRoute'],
    data() {
        return {
            companyId: null,
        };
    },
    computed: {
        /**
         * Combien de prévisions portent chaque article, lots compris, et pour quelle
         * quantité totale. Sert au rappel de non-cumul des paliers.
         */
        articleQuantities: function() {
            const totals = {};
            let predictions = (this.predictions || []).slice();

            (this.lots || []).forEach(lot => {
                predictions = predictions.concat(lot.predictions || []);
            });

            predictions.forEach(prediction => {
                if (prediction.is_title || !prediction.article_id) {
                    return;
                }

                const current = totals[prediction.article_id] || { count: 0, quantity: 0 };

                totals[prediction.article_id] = {
                    count: current.count + 1,
                    quantity: current.quantity + (parseFloat(prediction.quantity) || 0),
                };
            });

            return totals;
        }
    },
    methods: {
        /** Toutes les prévisions, hors lot et par lot, avec leur composant. */
        pricingPredictions() {
            var predictions = (this.predictions || []).slice();

            (this.lots || []).forEach(lot => {
                predictions = predictions.concat(lot.predictions || []);
            });

            return predictions
                .filter(prediction => prediction.article_id && !prediction.is_title)
                .map(prediction => ({ prediction: prediction, row: (this.$refs[prediction.uniq] || [])[0] }))
                .filter(item => item.row);
        },
        fetchPredictionPricing(items) {
            const params = new URLSearchParams();
            // Sans client, le serveur ne rend que la règle par défaut des articles.
            if (this.companyId) {
                params.append('company_id', this.companyId);
            }
            [...new Set(items.map(item => item.prediction.article_id))].forEach(id => params.append('article_ids[]', id));

            return fetch(this.pricingRoute + '?' + params.toString(), {
                ...{ method: 'GET' },
                ...this.getHttpJsonRequestHeaders()
            }).then(response => response.json());
        },
        /**
         * Les prévisions d'une étude rouverte viennent de la base : on apprend ce que la
         * politique en dirait sans rien réécrire, sinon rouvrir une étude en changerait les prix.
         */
        loadPredictionPricing() {
            if (!this.pricingRoute || !this.companyId) {
                return;
            }

            this.$nextTick(() => {
                const items = this.pricingPredictions();
                if (items.length === 0) {
                    return;
                }

                this.fetchPredictionPricing(items)
                    .then(data => this.adoptPredictionPricing(items, data.articles || {}))
                    .catch(error => console.log(error));
            });
        },
        adoptPredictionPricing(items, articles) {
            items.forEach(item => {
                const entry = articles[item.prediction.article_id] || null;

                item.row.adoptPricingEntry(pricingEntryOf(entry), entry ? entry.selling_price : null, entry);
            });
        },
        /**
         * La société change en cours de saisie : ni le profil ni les règles négociées des
         * articles déjà posés ne sont ceux du client. On les redemande pour proposer la
         * mise à jour, hors lignes reprises à la main.
         */
        refreshPredictionPricing(intro) {
            // Un changement de société sans société est le vidage transitoire de
            // l'autocomplete : rien à proposer. Un import, lui, a au moins la règle par défaut.
            if (!this.pricingRoute || (!this.companyId && !intro)) {
                return;
            }

            const items = this.pricingPredictions().filter(item => !item.row.edited_by_hand);
            if (items.length === 0) {
                return;
            }

            this.fetchPredictionPricing(items)
                .then(data => this.askPredictionPricingUpdate(items, data, intro))
                .catch(error => console.log(error));
        },
        /**
         * Des prévisions importées d'une autre étude ou d'une liste de prestations gardent
         * leurs prix d'origine, peut-être ceux d'un autre client : on propose ceux du client
         * de cette étude. Les lignes déjà au bon prix ne sont pas proposées.
         */
        repriceImportedPredictions() {
            this.$nextTick(() => this.refreshPredictionPricing(
                'Des prévisions ont été importées avec leurs prix d\'origine. Voulez-vous appliquer les prix du client de cette étude ?'
            ));
        },
        /** Un brouillon restauré porte les prix conseillés du jour de sa sauvegarde : on les relit. */
        reloadPredictionPricing() {
            this.companyId = $('#company_id').val() || this.companyId;
            this.loadPredictionPricing();
        },
        /** Ce que les conditions du nouveau client changeraient, ligne par ligne. */
        predictionPricingChanges(items, articles) {
            const changes = [];

            items.forEach(item => {
                const article = articles[item.prediction.article_id] || null;
                const entry = pricingEntryOf(article);
                const publicSelling = article ? article.selling_price : null;
                const suggested = suggestedNet(entry, item.prediction.quantity, publicSelling);

                // Sans droit chez ce client, une ligne au prix de l'ancien retombe au prix public.
                const selling = suggested ? suggested.net : publicSelling;

                if (selling !== null && selling !== undefined
                    && Math.abs((parseFloat(item.prediction.selling) || 0) - parseFloat(selling)) >= 0.0001) {
                    changes.push({ item: item, entry: entry, publicSelling: publicSelling, limits: article, selling: selling });
                }
            });

            return changes;
        },
        askPredictionPricingUpdate(items, data, intro) {
            const articles = data.articles || {};
            const changes = this.predictionPricingChanges(items, articles);

            // Les conditions du nouveau client décrivent le client, pas la valeur posée sur
            // la ligne : elles s'adoptent même quand rien n'est proposé.
            this.adoptPredictionPricing(items, articles);

            if (changes.length === 0) {
                return;
            }

            Swal.fire({
                title: data.profile ? 'Profil de remise ' + data.profile : 'Conditions du client',
                html: (intro || "La société de l'étude a été modifiée. Voulez-vous appliquer les prix de ce client ?") + '<br><b>'
                    + (changes.length > 1 ? changes.length + ' lignes changent de prix de vente' : '1 ligne change de prix de vente')
                    + '</b><br><small>Les lignes reprises à la main ne sont pas modifiées.</small>',
                icon: 'question',
                showCancelButton: true,
                cancelButtonText: 'Conserver les prix actuels',
                confirmButtonText: 'Appliquer les prix',
                reverseButtons: true,
                heightAuto: false
            }).then((result) => {
                if (result.value) {
                    changes.forEach(change => change.item.row.applyPricingChange(change.entry, change.publicSelling, change.limits, change.selling));
                    return;
                }

                // Prix conservés : ils cessent d'appartenir à la politique, sinon le premier
                // changement de quantité rappliquerait ce que l'utilisateur vient de refuser.
                changes.forEach(change => change.item.row.clearPricingSuggestion());
            });
        },
        /** La société du formulaire, et son changement en cours de saisie. */
        watchCompany() {
            if ($('#company_id').length === 0) {
                return;
            }

            this.companyId = $('#company_id').val() || null;
            this.loadPredictionPricing();

            $('#company_id').on('change', () => {
                this.companyId = $('#company_id').val() || null;
                this.refreshPredictionPricing();
            });
        }
    }
};
